import { useEffect, useMemo, useRef, useState } from 'react';

import useToast from '@/hooks/useToast';
import { useUploadFileMutation } from '@/store/rest/customersApi';
import { TAgent } from '@/types/agents';

import { EInputFieldType } from './types';

interface UseInputStageProps {
  agent: TAgent;
  inputValues: Record<string, string>;
  files: Record<string, File | File[]>;
  onInputValueChange: (key: string, value: string) => void;
  onFileChange: (key: string, file: File | File[] | null) => void;
}

const getFileKey = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

const useInputStage = ({ agent, inputValues, files, onInputValueChange, onFileChange }: UseInputStageProps) => {
  const {fromException} = useToast();
  const inputFieldsSchema = Object.entries(agent.schema.input);
  const [uploadFile] = useUploadFileMutation();
  const [loadingFieldKey, setLoadingFieldKey] = useState<string | null>(null);
  const uploadedFilesMapRef = useRef<Record<string, Record<string, string>>>({});
  const initializedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    Object.keys(files).forEach((key) => {
      const fileValue = files[key];
      const fileIdsString = inputValues[key];

      if (fileValue && fileIdsString && !initializedRef.current.has(key)) {
        const filesArray = Array.isArray(fileValue) ? fileValue : [fileValue];
        const fileIds = fileIdsString.split(',').filter(Boolean);

        if (filesArray.length === fileIds.length) {
          const fileMap: Record<string, string> = {};
          filesArray.forEach((f, index) => {
            const fileKey = getFileKey(f);
            fileMap[fileKey] = fileIds[index];
          });
          uploadedFilesMapRef.current[key] = fileMap;
          initializedRef.current.add(key);
        }
      }
    });
  }, [files, inputValues]);

  const isRunButtonDisabled = useMemo(() => {
    const inputSchemaKeys = Object.keys(agent.schema.input);

    for (const key of inputSchemaKeys) {
      if (!(key in inputValues)) {
        return true;
      }

      const schemaField = agent.schema.input[key];
      const value = inputValues[key];

      if (schemaField.type === EInputFieldType.FILE) {
        const fileValue = files[key];
        const hasFiles = Array.isArray(fileValue) ? fileValue.length > 0 : fileValue !== null && fileValue !== undefined;
        if (!hasFiles || !value || value.trim() === '') {
          return true;
        }
      } else {
        if (!value || value.trim() === '') {
          return true;
        }
      }
    }

    return false;
  }, [agent.schema.input, inputValues, files]);

  const handleFileChange = async (key: string, file: File | File[] | null) => {
    if (file) {
      const filesArray = Array.isArray(file) ? file : [file];
      const currentFileMap = uploadedFilesMapRef.current[key] || {};
      const existingFileIds: string[] = [];
      const newFiles: File[] = [];

      filesArray.forEach((f) => {
        const fileKey = getFileKey(f);
        if (currentFileMap[fileKey]) {
          existingFileIds.push(currentFileMap[fileKey]);
        } else {
          newFiles.push(f);
        }
      });

      const updatedFileMap: Record<string, string> = {};
      filesArray.forEach((f) => {
        const fileKey = getFileKey(f);
        if (currentFileMap[fileKey]) {
          updatedFileMap[fileKey] = currentFileMap[fileKey];
        }
      });

      if (newFiles.length > 0) {
        setLoadingFieldKey(key);
        try {
          const uploadPromises = newFiles.map((f) => uploadFile({ file: f }).unwrap());
          const newFileIds = await Promise.all(uploadPromises);

          newFiles.forEach((f, index) => {
            const fileKey = getFileKey(f);
            updatedFileMap[fileKey] = newFileIds[index];
          });
          uploadedFilesMapRef.current[key] = updatedFileMap;

          const allFileIds = [...existingFileIds, ...newFileIds];
          const fileIdsString = allFileIds.join(',');

          onFileChange(key, file);
          onInputValueChange(key, fileIdsString);
        } catch (error) {
          fromException({ error, label: 'Error uploading file' });

          const fileValue = files[key];
          const previousFiles: File[] = Array.isArray(fileValue)
            ? fileValue
            : fileValue
              ? [fileValue]
              : [];

          if (previousFiles.length > 0) {
            onFileChange(key, previousFiles.length === 1 ? previousFiles[0] : previousFiles);
            const previousFileIds = previousFiles
              .map((f) => currentFileMap[getFileKey(f)])
              .filter(Boolean) as string[];
            onInputValueChange(key, previousFileIds.join(','));
          } else {
            onFileChange(key, null);
            onInputValueChange(key, '');
            delete uploadedFilesMapRef.current[key];
          }
        } finally {
          setLoadingFieldKey(null);
        }
      } else {
        uploadedFilesMapRef.current[key] = updatedFileMap;
        const fileIdsString = existingFileIds.join(',');
        onFileChange(key, file);
        onInputValueChange(key, fileIdsString);
      }
    } else {
      onFileChange(key, file);
      onInputValueChange(key, '');
      delete uploadedFilesMapRef.current[key];
    }
  };

  return {
    handleFileChange,
    isRunButtonDisabled,
    loadingFieldKey,
    setLoadingFieldKey,
    inputFieldsSchema,
  };
};

export default useInputStage;